import { useLocalization } from "@fluent/react";
import { Tabs } from "@narmi/design_system";
import { useMemo, type ReactNode } from "react";
import { RolesProvider } from "byzantine/src/Role/RolesProvider";
import { OrganizationUserProvider } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import OrganizationUser from "byzantine/src/OrganizationUser";

import OrganizationUsers from "./OrganizationUsers";
import OrganizationRoles from "./OrganizationRoles";
import OrganizationEditRole from "./OrganizationEditRole";
import OrganizationNewRole from "./OrganizationNewRole";
import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import { SudoProvider } from "../SudoContext";
import SudoDialog from "../SudoDialog/SudoDialog";

import BaseBanner from "../BaseBanner";
import NewUserFlow from "./NewUserFlow";

type OrganizationUsersAndPermissionsProps = {
  currentOrganizationUser: OrganizationUser | null;
  organizationUuid?: UUID;
  orgId?: string;
  accounts?: (API.Account | API.ExternalAccount)[];
  externalAccounts?: (API.Account | API.ExternalAccount)[];
  features?: Record<string, any>;
  isUsingConsumerBillpayJson?: string;
};

const USERS_AND_PERMISSIONS_ROOT = "/permissions";

function UsersAndPermissionsTabsLayout({
  activeTab,
  children,
}: {
  activeTab: "users" | "roles";
  children?: ReactNode;
}) {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const selectedTabIndex = activeTab === "roles" ? 1 : 0;

  const handleTabChange = (newIndex: number) => {
    navigate(newIndex === 0 ? "/" : "/roles");
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <div className="organization-users-and-permissions-banner">
        <BaseBanner
          bannerName={l10n.getString("banner-users-and-permissions")}
          features={{ business_banking: true }}
          orFeatureFlagChecks={["business_banking"]}
        />
      </div>
      <Tabs selectedIndex={selectedTabIndex} onTabChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab label={l10n.getString("org-tab-users")} tabId="users" />
          <Tabs.Tab label={l10n.getString("org-tab-roles")} tabId="roles" />
        </Tabs.List>
      </Tabs>
      {children ?? <Outlet />}
    </div>
  );
}

export default function OrganizationUsersAndPermissions({
  currentOrganizationUser,
  organizationUuid,
  orgId,
  accounts = [],
  externalAccounts = [],
  features,
  isUsingConsumerBillpayJson,
}: OrganizationUsersAndPermissionsProps) {
  const effectiveOrgId = orgId || organizationUuid || "";
  const allAccounts = useMemo(
    () => [...accounts, ...externalAccounts],
    [accounts, externalAccounts]
  );

  return (
    <BrowserRouter basename={USERS_AND_PERMISSIONS_ROOT}>
      <SudoProvider>
        <InstitutionSettingsContextProvider>
          <RolesProvider orgUuid={effectiveOrgId as UUID}>
          <OrganizationUserProvider orgUuid={effectiveOrgId as UUID}>
            <Routes>
              <Route
                path="/new"
                element={
                  <NewUserFlow
                    organizationUuid={effectiveOrgId as UUID}
                    allAccounts={allAccounts}
                  />
                }
              />
              <Route
                element={<UsersAndPermissionsTabsLayout activeTab="users" />}
              >
                <Route
                  path="/:userUuid?"
                  element={
                    <OrganizationUsers
                      currentOrganizationUser={currentOrganizationUser}
                    />
                  }
                />
              </Route>
              <Route
                element={<UsersAndPermissionsTabsLayout activeTab="roles" />}
              >
                <Route
                  path="/roles/:roleUuid?"
                  element={
                    <OrganizationRoles
                      organizationUuid={effectiveOrgId as UUID}
                      allAccounts={allAccounts}
                    />
                  }
                />
              </Route>
              <Route
                path="/roles/new"
                element={
                  <OrganizationNewRole
                    orgId={effectiveOrgId}
                    features={features}
                    accounts={accounts as any}
                    externalAccounts={externalAccounts as any}
                    isUsingConsumerBillpayJson={isUsingConsumerBillpayJson}
                  />
                }
              />
              <Route
                path="/roles/:roleUuid/edit"
                element={
                  <OrganizationEditRole
                    orgId={effectiveOrgId}
                    features={features}
                    accounts={accounts as any}
                    externalAccounts={externalAccounts as any}
                    isUsingConsumerBillpayJson={isUsingConsumerBillpayJson}
                  />
                }
              />
              <Route
                path="/roles/:roleUuid/duplicate"
                element={
                  <OrganizationNewRole
                    orgId={effectiveOrgId}
                    features={features}
                    accounts={accounts as any}
                    externalAccounts={externalAccounts as any}
                    isUsingConsumerBillpayJson={isUsingConsumerBillpayJson}
                    isDuplicate
                  />
                }
              />
            </Routes>
          </OrganizationUserProvider>
          </RolesProvider>
        </InstitutionSettingsContextProvider>
        <SudoDialog />
      </SudoProvider>
    </BrowserRouter>
  );
}
